import {
  Campaign,
  useCampaign,
  useReport,
  useReports,
  UseReportsProps,
  GetCampaignParams,
  UseCalculatedFieldsReturn,
  useCalculatedFields,
} from '@features/campaigns';
import { useLatestCampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/hooks/useCampaignAnalytics';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { GetLci3ReportParams } from '@components/Dashboard/dashboardSlice/campaignsApi';
import {
  sortBySalesLift,
  sortByVisitLift,
  Subdimension,
  subdimensionsByName,
  SubdimensionsByName,
} from '../../../../reports/subdomains/visitation/domain/dimensions';
import { SubdimensionCounts, validDimensionSlug } from '@features/reports';
import { TOTAL_DIMENSIONS } from '@features/executive-report/dimensions/layout';
import { EXECUTIVE_REPORT_DIMENSION_SLUG_SET } from '@features/executive-report/domain/dimensions';
import { useMemo } from 'react';

const topDimensionsByNameSorted =
  (subdimensionsByName: SubdimensionsByName) =>
  (sortFunction: (a: Subdimension, b: Subdimension) => number) =>
    Object.entries(subdimensionsByName).reduce(
      (acc, [dimensionSlug, subdimensions]) => {
        if (!validDimensionSlug(dimensionSlug)) return acc;

        if (!EXECUTIVE_REPORT_DIMENSION_SLUG_SET.has(dimensionSlug)) return acc;

        acc[dimensionSlug] = subdimensions
          .toSorted(sortFunction)
          .slice(0, TOTAL_DIMENSIONS);
        return acc;
      },
      {} as SubdimensionsByName
    );

export type UseExecutiveReportFieldsHook = {
  loading: boolean;
  error?: string;
  data?: {
    campaign: Pick<
      Campaign,
      'id' | 'name' | 'startDate' | 'endDate' | 'youtubeMeasurementEnabled'
    >;
    currentReport: Pick<
      Lci3Report,
      'salesMetrics' | 'visitMetrics' | 'startDate' | 'reportDate'
    >;
    currentSalesMetrics: Lci3Report['salesMetrics'];
    midCampaignSalesMetrics: Lci3Report['salesMetrics'];
    campaignAnalyticsReport: CampaignAnalyticsReport | null;
    midCampaignIncrementalLift: number | null;
    topVisitSubdimensionsByName: SubdimensionsByName;
    topSalesSubdimensionsByName: SubdimensionsByName;
    subdimensionCounts: SubdimensionCounts;
    calculatedFields: UseCalculatedFieldsReturn['data'];
  };
};

type FetchParams = {
  campaignParams: GetCampaignParams;
  reportParams: Omit<GetLci3ReportParams, 'id'>;
};

export const useExecutiveReportFields = (
  fetchParams: FetchParams
): UseExecutiveReportFieldsHook => {
  const { campaign, isFetching: isFetchingCampaign } = useCampaign(
    fetchParams.campaignParams
  );
  const reportsProps: UseReportsProps = {
    campaignId: campaign?.id,
    campaignLoading: isFetchingCampaign,
    reportParams: fetchParams.reportParams,
    campaignParams: fetchParams.campaignParams,
  };
  const {
    report,
    loading: isFetchingReport,
    salesReport,
    subdimensions,
  } = useReport(reportsProps);
  const { reports } = useReports(reportsProps);

  const {
    report: campaignAnalyticsReport,
    isLoading: isFetchingCampaignAnalyticsReport,
  } = useLatestCampaignAnalyticsReport(fetchParams.campaignParams.id);

  const { data: calculatedFields, isFetching: isFetchingCalculatedFields } =
    useCalculatedFields({
      report,
      salesReport,
      campaign: campaign ?? { startDate: '', endDate: '', id: 0 },
    });

  const loading =
    isFetchingCampaign ||
    isFetchingReport ||
    isFetchingCampaignAnalyticsReport ||
    isFetchingCalculatedFields;

  const {
    midCampaignSalesMetrics,
    midCampaignIncrementalLift,
    topVisitSubdimensionsByName,
    topSalesSubdimensionsByName,
    subdimensionCounts,
  } = useMemo(() => {
    const reportsWithSales = reports
      .filter(report => report.salesMetrics)
      .toSorted((a, b) => Date.parse(b.reportDate) - Date.parse(a.reportDate));

    const midCampaignSalesMetrics =
      reportsWithSales[Math.floor(reportsWithSales.length / 2)]?.salesMetrics ??
      null;

    const midCampaignIncrementalLift =
      reports.at(Math.floor(reports.length / 2))?.visitMetrics
        .incrementalLift ?? null;

    const allSubdimensions = subdimensionsByName(subdimensions);
    const getSubdimensionsSorted = topDimensionsByNameSorted(allSubdimensions);
    const topVisitSubdimensionsByName = getSubdimensionsSorted(sortByVisitLift);
    const topSalesSubdimensionsByName = getSubdimensionsSorted(sortBySalesLift);

    const subdimensionCounts = Object.entries(allSubdimensions).reduce(
      (acc, [dimensionSlug, subdimensions]) => {
        if (!validDimensionSlug(dimensionSlug)) return acc;
        acc[dimensionSlug] = subdimensions.length;
        return acc;
      },
      {} as SubdimensionCounts
    );

    return {
      midCampaignSalesMetrics,
      midCampaignIncrementalLift,
      topVisitSubdimensionsByName,
      topSalesSubdimensionsByName,
      subdimensionCounts,
    };
  }, [reports, subdimensions]);

  if (!campaign || !report) return { error: 'Missing data', loading };

  return {
    loading,
    data: {
      campaign,
      currentReport: report,
      currentSalesMetrics: salesReport?.salesMetrics ?? null,
      midCampaignSalesMetrics,
      campaignAnalyticsReport: campaignAnalyticsReport ?? null,
      midCampaignIncrementalLift,
      topVisitSubdimensionsByName,
      topSalesSubdimensionsByName,
      subdimensionCounts,
      calculatedFields,
    },
  };
};
